import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, isSameDay, isAfter, startOfDay, endOfDay, addHours, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';
import { useSubscription } from '../../hooks/useSubscription';
import {
  Calendar,
  MessageSquare,
  Mic,
  Send,
  StopCircle,
  User,
  Clock,
  Scissors,
  Ban,
  Check,
  X,
  AlertTriangle,
  UserX,
  Trash2,
  AlertCircle,
  LogOut,
  Settings,
  Users,
  BarChart2,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import { AppointmentDetails } from '../../components/appointments/DayView/components/AppointmentDetails';
import { requestMicrophonePermission, startRecording } from '../../utils/microphone';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { upsertSubscription } from '../../lib/supabase/subscription';
import LoadingScreen from '../../components/LoadingScreen';
import StatisticsPage from '../Statistics'; // ודא שיש קובץ כזה
import AutofillUI from '../../components/AutofillScheduler';
import { LoLytaAI } from '../../components/ai-chat/LoLytaAI';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TokensInfo {
  available: boolean;
  tokens_limit: number | null;
  tokens_used: number;
  tokens_remaining: number | null;
  display_limit: number | null;
  display_used: number | null;
  display_remaining: number | null;
  tokens_last_reset?: string; // <-- הוסף שדה זה
}

interface Appointment {
  id: string;
  business_id: string;
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  service_id: string;
  service_name: string;
  staff_id: string;
  staff_name: string;
  start_time: string;
  end_time: string;
  duration: string;
  price: number;
  status: string;
  avatar: string;
}

const QUICK_REPLIES = [
  'מה התורים שלי היום?',
  'מי הלקוחות שלא אישרו הגעה?',
  'שלח עכשיו תזכורת לכל מי שיש לו תור מחר',
  'עדכן אותי אם יש ביטולים או תורים שהתפנו היום',
  'כמה תורים בוטלו השבוע?',
  'מי הלקוחות שביקשו לקבוע תור ועדיין לא סגרו?',
  'מה ההכנסות שלי היום עד עכשיו?',
  'כמה תורים פנויים נשארו לי השבוע?',
  'עדכן את שעות הפעילות שלי להיום',
];

// פונקציה לבדוק האם הצ'אט חסום (העבר אותה לפני הפונקציה Dashboard)
function isChatBlocked(tokensInfo: TokensInfo | null, isDashboardPage: boolean, trialAvailable: boolean) {
  // חסום רק אם tokensInfo.available === false או display_remaining === 0 (ולא בתקופת ניסיון)
  if (!isDashboardPage) return false;
  if (trialAvailable) return false;
  if (!tokensInfo) return true; // אין מידע בכלל - תחסום
  if (tokensInfo.available === false) return true;
  if (tokensInfo.display_limit !== null && tokensInfo.display_remaining === 0) return true;
  return false;
}

function isUserBlockedFromAIAgent(user: any, businessPlanId: string | null): boolean {
  const basicPlanId = '686adae1-c8ab-46a8-aea4-d5a78e431723';
  // חסום אם businessPlanId חסר או שהוא בדיוק basic
  return !businessPlanId || businessPlanId === basicPlanId;
}

// ---- Tokens helpers & plan limits ----
type PlanLimits = {
  ai_tokens_limit: number | null;
  ai_display_limit: number | null;
};

async function fetchPlanLimitsById(planId: string): Promise<PlanLimits | null> {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('ai_tokens_limit, ai_display_limit')
    .eq('id', planId)
    .single();

  if (error) {

    return null;
  }
  return {
    ai_tokens_limit: data?.ai_tokens_limit ?? null,
    ai_display_limit: data?.ai_display_limit ?? null,
  };
}

function fmtLeftOutOf(
  limit: number | null | undefined,
  remaining: number | null | undefined,
  used?: number | null | undefined
) {
  if (limit == null) return 'ללא תקרה';
  const left = remaining != null ? remaining : Math.max(0, limit - (used ?? 0));
  return `${left.toLocaleString('he-IL')} / ${limit.toLocaleString('he-IL')}`;
}

function percentLeft(
  limit: number | null | undefined,
  remaining: number | null | undefined,
  used?: number | null | undefined
) {
  if (limit == null) return 100;
  const left = remaining != null ? remaining : Math.max(0, limit - (used ?? 0));
  return Math.max(0, Math.min(100, Math.round((100 * left) / Math.max(1, limit))));
}

function showBar(limit: number | null | undefined) {
  return typeof limit === 'number' && limit > 0;
}
// ---- end helpers ----

function Dashboard() {
  const { trialAvailable } = useSubscription();
  const { isTrialStillValid } = useSubscription();
  const { user, business, signOut, isDashboardPage, refreshBusiness } = useAuth(); // הוסף refreshBusiness
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const planName = subscription?.plan?.name || null;
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [quickReplies, setQuickReplies] = useState(QUICK_REPLIES);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState<NodeJS.Timeout | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [lastMessageId, setLastMessageId] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [tokensInfo, setTokensInfo] = useState<TokensInfo | null>(null);
  const [chatChannel, setChatChannel] = useState<any>(null);
  const [appointmentsChannel, setAppointmentsChannel] = useState<any>(null);
  const [tokensChannel, setTokensChannel] = useState<any>(null);
  const [businessPlanId, setBusinessPlanId] = useState<string | null>(null);
  const [planCode, setPlanCode] = useState<string | null>(null);
   // MCP Agent state
  const [mcpLoading, setMcpLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [deleteWarning, setDeleteWarning] = useState('');
const [pendingDelete, setPendingDelete] = useState<{ message: string; sql?: string } | null>(null);
const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
const [showAutofillPopup, setShowAutofillPopup] = useState(false);

  const canRecord = window.isSecureContext && 'MediaRecorder' in window && navigator.mediaDevices?.getUserMedia;
  // האם עדיין טוען את סטטוס החסימה (כל עוד לא נטענו הערכים הקריטיים)
  const isLoadingBlockStatus = isDashboardPage && (
    (tokensInfo === null && !trialAvailable) ||
    (businessPlanId === null && business?.subscription_id)
  );

  // חסום צ'אט לפי סוג החבילה בלבד (בזמן אמת)
  // שמור את סוג החבילה במשתנה גלובלי בתוך הפונקציה Dashboard
  const planType = (subscription?.plan?.name || subscription?.plan?.code || "").trim().toLowerCase();

  // הצ'אט חסום כברירת מחדל
  let blocked = true;
  let userBlocked = true;

  // פתח את הגישה לצ'אט רק אם יש תקופת ניסיון פעילה או החבילה אינה בסיסית
  if (trialAvailable) {
    blocked = false;
    userBlocked = false;
  } else if (planType !== "בסיסית" && planType !== "basic" && planType !== "basic plan") {
    blocked = false;
    userBlocked = false;
  }

  // חסום את הצ'אט גם אם tokensInfo.available === false
  if (tokensInfo && tokensInfo.available === false) {
    blocked = true;
    userBlocked = true;

  }

  // הסר כל קוד של רענון דף אוטומטי (useEffect עם reload)
  // האם המשתמש חסום מה-AI? (לפי businessPlanId)
  const navigate = useNavigate();
  const [trialCountdown, setTrialCountdown] = useState<string | null>(null);

  // טיימר לסיום 14 ימי ניסיון לפי user.created_at
  useEffect(() => {
    if (!user?.created_at || !trialAvailable) {
      setTrialCountdown(null);
      return;
    }

    

      

  
    const createdAt = new Date(user.created_at);
    const trialEnd = new Date(createdAt.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 ימים
    const updateCountdown = () => {
      const now = new Date();
      const diff = trialEnd.getTime() - now.getTime();
      if (diff <= 0) {
        setTrialCountdown('תקופת הניסיון הסתיימה');
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      setTrialCountdown(
        days > 0
          ? `נותרו ${days} ימים, ${hours} שעות, ${minutes} דקות לסיום תקופת הניסיון`
          : `נותרו ${hours} שעות, ${minutes} דקות לסיום תקופת הניסיון`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [user?.created_at, trialAvailable]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;

    } else {

    }
  }, []);

  const creatingBusinessOnce = useRef(false);
 useEffect(() => {
  if (!user?.id || business?.id || creatingBusinessOnce.current) return;
  creatingBusinessOnce.current = true;

  (async () => {
    try {
      // 1) האם המשתמש כבר משויך לעסק?
      const { data: userRow, error: userErr } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (userErr) {
        creatingBusinessOnce.current = false;
        return;
      }

      if (userRow?.business_id) {
        localStorage.setItem('businessId', userRow.business_id);
        setBusinessId(userRow.business_id);
        creatingBusinessOnce.current = false;
        return;
      }

      // 2) יצירת עסק חדש (Fallback מהטיוטה)
      const draft = localStorage.getItem('registration_draft');
      let step2: any = undefined;
      try { step2 = draft ? JSON.parse(draft)?.step2 : undefined; } catch {}

      const fallbackName =
        (typeof step2?.name === 'string' && step2.name.trim())
          ? step2.name.trim()
          : `העסק של ${user.user_metadata?.name || 'משתמש חדש'}`;

      const fallbackType =
        (typeof step2?.type === 'string' && step2.type.trim())
          ? step2.type.trim()
          : 'general';

      const { data: biz, error: bizErr } = await supabase
        .from('businesses')
        .insert({
          owner_id: user.id,
          name: fallbackName,
          type: fallbackType,
          description: (typeof step2?.description === 'string') ? step2.description : '',
          address: (typeof step2?.address === 'string') ? step2.address : '',
          settings: { theme: 'light', notifications: true },
          logo_url: step2?.logo_path || null
        })
        .select('id')
        .single();

      if (bizErr || !biz?.id) {

        creatingBusinessOnce.current = false;
        return;
      }

      const newBusinessId = biz.id;
      localStorage.setItem('businessId', newBusinessId);
      setBusinessId(newBusinessId);

      // 3) קישור המשתמש לעסק
      const { error: linkErr } = await supabase
        .from('users')
        .update({ business_id: newBusinessId })
        .eq('id', user.id);

      if (linkErr) {

        creatingBusinessOnce.current = false;
        return;
      }

      // (רצוי באותיות קטנות כדי להתאים למדיניות RLS, אם יש)
      await supabase.auth.updateUser({ data: { role: 'admin' } });

      // 4) ❌ REMOVED - DO NOT create subscription from localStorage!
      //    Subscriptions should ONLY be created via:
      //    1. Stripe webhook after payment
      //    2. insertDefaultSubscription() for trial subscriptions
      //
      //    localStorage is NEVER the source of truth for subscriptions!

      // 5) (אופציונלי) מיגרציית לוגו וכו׳…

    } catch (e) {

    } finally {
      creatingBusinessOnce.current = false;
    }
  })();
}, [user?.id, business?.id, refreshBusiness]);

  // Function to add message
  const addMessage = useCallback((newMessage: Message) => {
    setMessages(prev => {
      // Check if message already exists
      if (prev.some(msg => msg.id === newMessage.id)) {
        return prev;
      }
      const newMessages = [...prev, newMessage];
      // Scroll after state update
      setTimeout(scrollToBottom, 100);
      return newMessages;
    });
  }, [scrollToBottom]);

  const sentClientIds = useRef<Set<string>>(new Set());   // holds assistant ids we've already shown (dedup)
const lastClientReplyIdRef = useRef<string | null>(null);

const pickAssistantMsgId = (row: any): string => {
  return row?.meta?.client_reply_id || row?.meta?.reply_id || String(row.id);
};

// Subscribe to chat responses
  const subscribeToChat = useCallback(() => {
    if (!user?.id) return;

    const channel = supabase
  .channel('chat_responses')
  .on(
    'postgres_changes',
    {
       event: '*', // <- לא רק INSERT
      schema: 'public',
      table: 'chat_histories',
      filter: `user_id=eq.${user.id}`
    },
    (payload) => {
      try {
        const row = payload.new;
        if (row.role !== 'assistant') return;

        const dedupId = pickAssistantMsgId(row);
        if (sentClientIds.current.has(dedupId)) return;

        addMessage({
          id: dedupId,
          role: 'assistant',
          content: row.content,
          timestamp: new Date(row.created_at)
        });
        sentClientIds.current.add(dedupId);
        setIsTyping(false);
      } catch (error) {

      }
    }
  )
  .subscribe((status) => {

  });

    setChatChannel(channel);
    return () => {
      channel.unsubscribe();
    };
  }, [user?.id, addMessage]);

  // Subscribe to appointment changes
  const subscribeToAppointments = useCallback((id: string) => {
    if (!user?.id) return;

    const channel = supabase
      .channel('appointments_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `staff_id=eq.${user.id}`
        },
        (payload) => {

          // Refresh appointments when any change occurs
          loadAppointments(id);
        }
      )
      .subscribe((status) => {

      });

    setAppointmentsChannel(channel);
    return () => {
      channel.unsubscribe();
    };
  }, [user?.id]);

  // Subscribe to token usage changes
  const subscribeToTokenUsage = useCallback((id: string) => {
    if (!user?.id) return;

    const channel = supabase
      .channel('token_usage_changes')
      .on(
        'postgres_changes',
        {
           event: '*', 
          schema: 'public',
          table: 'token_usage',
          filter: `business_id=eq.${id}`
        },
        (payload) => {

          // Refresh token info when usage changes
          loadTokensInfo(id);
        }
      )
      .subscribe((status) => {

      });

    setTokensChannel(channel);
    return () => {
      channel.unsubscribe();
    };
  }, [user?.id]);

  // Load initial data
  useEffect(() => {
    if (isInitialized) return;

    const initializeChat = async () => {
      // Set initial welcome message

      if (business?.id) {
        setBusinessId(business.id);
        await loadAppointments(business.id);
        const unsubscribeAppointments = subscribeToAppointments(business.id);
        if (isDashboardPage) {
          await loadTokensInfo(business.id);
          const unsubscribeTokens = subscribeToTokenUsage(business.id);
        }
      } else if (user?.id) {
        await fetchBusinessId();
      }

      // אל תטען היסטוריית צ'אט:
      // await loadChatHistory();

      const unsubscribeChat = subscribeToChat();

      setIsInitialized(true);

      return () => {
        if (unsubscribeChat) unsubscribeChat();
        if (chatChannel) chatChannel.unsubscribe();
        if (appointmentsChannel) appointmentsChannel.unsubscribe();
        if (tokensChannel) tokensChannel.unsubscribe();
      };
    };

    initializeChat(); 
  }, [user?.id, business?.id, isInitialized, subscribeToChat, subscribeToAppointments, subscribeToTokenUsage, isDashboardPage]);
useEffect(() => {
    if (user?.id && businessId) {
      loadChatHistory();
    }
  }, [user?.id, businessId]);

  // ✅ בדיקה אם חזרנו מ-Stripe עם session_id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId && business?.id) {

      const createSubscriptionFromLocalStorage = async () => {
        try {
          const selectedPlan = localStorage.getItem('selectedPlan');
          const selectedPlanCycle = localStorage.getItem('selectedPlanCycle') || 'monthly';

          if (!selectedPlan) {

            return;
          }

          // שלוף את ה-plan_id לפי plan_key
          const { data: planData, error: planError } = await supabase
            .from('subscription_plans')
            .select('id, code, name')
            .eq('plan_key', selectedPlan)
            .single();

          if (planError || !planData) {

            toast.error('שגיאה במציאת החבילה');
            return;
          }

          // בדוק אם כבר יש subscription
          const { data: existingSub } = await supabase
            .from('subscriptions')
            .select('id')
            .eq('business_id', business.id)
            .maybeSingle();

          if (existingSub) {

            toast.success('המנוי כבר קיים במערכת!');

            // נקה localStorage
            localStorage.removeItem('selectedPlan');
            localStorage.removeItem('selectedPlanCycle');

            // רענן דף
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 1500);
            return;
          }

          // צור subscription חדש
          const { data: newSub, error: subError } = await supabase
            .from('subscriptions')
            .insert({
              business_id: business.id,
              plan_id: planData.id,
              status: 'active',
              billing_cycle: selectedPlanCycle,
              current_period_start: new Date().toISOString(),
              current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            })
            .select()
            .single();

          if (subError) {

            toast.error('שגיאה ביצירת המנוי');
            return;
          }

          toast.success(`התשלום בוצע בהצלחה! מנוי ${planData.name} פעיל.`);

          // נקה localStorage
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('selectedPlanCycle');

          // רענן דף
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
        } catch (error) {

          toast.error('שגיאה ביצירת המנוי');
        }
      };

      createSubscriptionFromLocalStorage();
    }
  }, [business?.id]);

  // שמור את מזהה העסק ב-localStorage מיד אחרי טעינת business מה-context
  // ❌ REMOVED - DO NOT upsert subscription from localStorage!
  //    This useEffect was causing subscriptions to be overwritten with 'basic'
  //    whenever the Dashboard loaded.
  //
  //    Database is the source of truth. App.tsx already syncs localStorage FROM Database.
  useEffect(() => {
    if (business?.id) {
      localStorage.setItem('businessId', business.id);

    }
  }, [business?.id, user?.id]);

  // Fetch plan_id from subscriptions when business.subscription_id changes
  useEffect(() => {
    async function fetchPlanId() {
      if (business?.subscription_id) {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .eq('id', business.subscription_id)
          .single();
        if (!error && data?.plan_id) {
          setBusinessPlanId(data.plan_id.toString().trim());
        } else {
          setBusinessPlanId(null);
        }
      } else {
        setBusinessPlanId(null);
      }
    }
    fetchPlanId();
  }, [business?.subscription_id]);

  // הצג את סוג החבילה בדשבורד (לבדיקה)
  useEffect(() => {
    async function fetchPlanCode() {
      if (businessPlanId) {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('code')
          .eq('id', businessPlanId)
          .single();
        if (!error && data?.code) {
          setPlanCode(data.code);
        } else {
          setPlanCode(null);
        }
      } else {
        setPlanCode(null);
      }
    }
    fetchPlanCode();
  }, [businessPlanId]);
const loadTokensInfo = async (bizId: string) => {

  // עוזרת למפות שמות שדות שונים (remaining / tokens_remaining) לאותו טיפוס
  const normalize = (raw: any): TokensInfo | null => {
    if (!raw) return null;
    const remaining =
      raw.tokens_remaining ??
      raw.remaining ??
      null;

    const mapped: TokensInfo = {
      available: raw.available ?? true,
      tokens_limit: raw.tokens_limit ?? null,
      tokens_used: raw.tokens_used ?? 0,
      tokens_remaining: remaining,
      display_limit: raw.display_limit ?? null,
      display_used: raw.display_used ?? 0,
      display_remaining: raw.display_remaining ?? null,
      tokens_last_reset: raw.tokens_last_reset ?? raw.last_reset ?? undefined,
    };
    return mapped;
  };

  // קריאה ל-RPC המעודכן
  try {

    const { data, error } = await supabase.rpc('get_remaining_tokens', {
      p_business_id: bizId,
    });

    if (error) throw error;

    if (data && data.available) {
      const mapped = {
        tokens_limit: data.tokens_limit || 0,
        tokens_used: data.tokens_used || 0,
        tokens_remaining: data.tokens_remaining || 0,
        display_limit: data.tokens_limit || 0,
        display_used: data.tokens_used || 0,
        display_remaining: data.tokens_remaining || 0,
        tokens_last_reset: undefined,
      };

      setTokensInfo(mapped);
      return;
    }

    setTokensInfo(null);
  } catch (err) {

    setTokensInfo(null);
  }
};

const loadChatHistory = async () => {
  if (!user?.id || !businessId) return;
  try {
    const { data, error } = await supabase
      .from('chat_histories')
      .select('*, meta')
      .eq('user_id', user.id)
      .eq('business_id', businessId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    if (!data || data.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'היי! אני לוליטה, העוזרת החכמה שלך 😊 איך אוכל לעזור לך היום?',
        timestamp: new Date()
      }]);
      setTimeout(scrollToBottom, 100);
      return;
    }

    const formattedMessages: Message[] = data.map((msg) => {
      const id = msg.role === 'assistant' ? pickAssistantMsgId(msg) : msg.id.toString();
      if (msg.role === 'assistant') {
        sentClientIds.current.add(id);
      }
      return {
        id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
      };
    });

    setMessages(formattedMessages);
    setTimeout(scrollToBottom, 100);
  } catch (error) {

  }
};

  const fetchBusinessId = async () => {
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user?.id)
        .single();

      if (userError) throw userError;
      if (!userData?.business_id) throw new Error('לא נמצא עסק מקושר');

      setBusinessId(userData.business_id);
      loadAppointments(userData.business_id);
      
      // Load tokens info only if we're on the dashboard
      let unsubscribeTokens: (() => void) | undefined;
      if (isDashboardPage) {
        await loadTokensInfo(userData.business_id);
        // Subscribe to token usage changes
        unsubscribeTokens = subscribeToTokenUsage(userData.business_id);
      }
      
      // Subscribe to appointment changes
      const unsubscribeAppointments = subscribeToAppointments(userData.business_id);
      
      return () => {
        if (unsubscribeAppointments) unsubscribeAppointments();
        if (unsubscribeTokens) unsubscribeTokens();
      };
    } catch (error) {

      // toast.error('שגיאה בטעינת נתוני העסק');
      setLoadingAppointments(false);
    }
  };

  const loadAppointments = async (id: string) => {
    try {
      setLoadingAppointments(true);

      // הדפס user.id ו-businessId לבדיקה

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextWeek = addDays(today, 7);

      // נסה לשלוף תורים לפי business_id בלבד, ללא פילטר סטטוס בכלל
      const { data: appointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select(`
          id,
          business_id,
          customer_id,
          staff_id,
          service_id,
          start_time,
          end_time,
          status,
          customers (
            name,
            phone
          ),
          services (
            id,
            name_he,
            price,
            duration
          ),
          users (
            name
          )
        `)
        .eq('business_id', id)
        .gte('start_time', today.toISOString())
        .lt('start_time', nextWeek.toISOString())
        // .in('status', ['booked', 'confirmed', 'completed']) // הסר את הפילטר הזה
        .order('start_time')
        .limit(20);

      if (appointmentsError) throw appointmentsError;

      const now = new Date();
      const CANCELLED_STATUSES = ['completed', 'cancelled', 'Canceled', 'canceled', 'בוטל'];
      const activeAppointments = (appointments || [])
        .filter(apt => isAfter(parseISO(apt.end_time), now) && !CANCELLED_STATUSES.includes(apt.status))
        .map(apt => {
          // תקן גישה ל-customers ו-services במקרה שזה מערך ולא אובייקט
          let customerName = '';
          let customerPhone = '';
          if (Array.isArray(apt.customers)) {
            customerName = apt.customers[0]?.name || '';
            customerPhone = apt.customers[0]?.phone || '';
          } else {
            customerName = apt.customers?.name || '';
            customerPhone = apt.customers?.phone || '';
          }

          let serviceId = '';
          let serviceName = '';
          let servicePrice = 0;
          let serviceDuration = '';
          if (Array.isArray(apt.services)) {
            serviceId = apt.services[0]?.id || '';
            serviceName = apt.services[0]?.name_he || '';
            servicePrice = apt.services[0]?.price || 0;
            serviceDuration = apt.services[0]?.duration || '';
          } else {
            serviceId = apt.services?.id || '';
            serviceName = apt.services?.name_he || '';
            servicePrice = apt.services?.price || 0;
            serviceDuration = apt.services?.duration || '';
          }

          return {
            id: apt.id,
            business_id: apt.business_id,
            customer_id: apt.customer_id,
            customer_name: customerName,
            customer_phone: customerPhone,
            service_id: serviceId,
            service_name: serviceName,
            staff_id: apt.staff_id,
            staff_name: apt.users?.name || '',
            start_time: apt.start_time,
            end_time: apt.end_time,
            duration: serviceDuration,
            price: servicePrice,
            status: apt.status,
            avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(customerName)}`
          };
        });

      setAppointments(activeAppointments);
    } catch (error) {

      // toast.error('שגיאה בטעינת התורים');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const shuffleQuickReplies = () => {
    setQuickReplies(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  const sendMessageToWebhook = async (message: string, type: string = 'text', recordingUrl?: string) => {
    if (!user?.id || !businessId) {
      throw new Error('חסרים פרטי משתמש או עסק');
    }

    try {
      // First log message to database
      const { data: logResult, error: logError } = await supabase
        .rpc('log_business_message', {
          p_user_id: user.id,
          p_business_id: businessId,
          p_message: type === 'audio' ? recordingUrl : message,
          p_message_type: type
        });

      if (logError) throw logError;

      if (!logResult.success) {
        throw new Error(logResult.error || 'שגיאה בשמירת ההודעה');
      }

      // Then send to webhook
      const response = await fetch('https://ofekperetz.app.n8n.cloud/webhook/b0e5c037-e24a-4680-8fb1-843d69a23d1c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          user_id: user.id,
          chat_id: user.id,
          message: type === 'audio' ? recordingUrl : message,
          message_type: type,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return await response.json();
    } catch (error) {

      throw error;
    }
  };
// עדכן את handleSendMessage שישלח ל-MCP Agent
// ========= handleSendMessage =========

const handleSendMessage = async (text?: string) => {
  const messageContent = text || newMessage;
  if (!messageContent.trim()) return;

  if (isDashboardPage && tokensInfo && !tokensInfo.available && !trialAvailable) return;
  if (userBlocked) {
    toast.error('גישה לצ\'אט AI חסומה למשתמשים בתקופת ניסיון.');
    return;
  }

  const userMessage: Message = {
    id: crypto.randomUUID(),
    role: 'user',
    content: messageContent,
    timestamp: new Date()
  };

  addMessage(userMessage);
  setNewMessage('');
  shuffleQuickReplies();
  setIsTyping(true);
  setMcpLoading(true);

  const clientReplyId = crypto.randomUUID();
  lastClientReplyIdRef.current = clientReplyId;

  try {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const res = await fetch('https://nkuqcyelxgyihrxyvitb.functions.supabase.co/mcp-agent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        businessId,
        userId: user?.id,
        message: messageContent,
        confirm: false,
        // שולח גם camel וגם snake למקרה שהפונקציה בצד שרת מצפה ל-snake:
        clientReplyId,
        client_reply_id: clientReplyId,
        client_user_id: userMessage.id,
      }),
    });

    const data = await res.json();

    // א) צריך אישור מחיקה -> פותחים מודל וגם מכבים ספינר
    if (data?.requireConfirmation) {
      setDeleteWarning(data.warning || 'האם לאשר מחיקה?');
      setPendingDelete({ message: messageContent, sql: data.sql ?? undefined });
      setShowDeleteModal(true);
      setIsTyping(false);
      setMcpLoading(false);
      return;
    }

    // ב) חוסר בהירות/דיוק זהות (disambiguation) -> מציגים הודעה ידידותית ומכבים ספינר
    if (
      data?.missingField === 'disambiguation' ||
      data?.requireDisambiguation ||
      data?.type === 'disambiguation'
    ) {
      const baseMsg =
        data?.message ||
        'צריך לזהות במדויק את הלקוח. להציג רשימת התאמות לשם שביקשת?';

      addMessage({
        id: clientReplyId,
        role: 'assistant',
        content: baseMsg,
        timestamp: new Date(),
      });

      // אם השרת מחזיר candidates נציג אותם כרשימה טקסטואלית
      if (Array.isArray(data?.candidates) && data.candidates.length) {
        const lines = data.candidates.map((c: any, i: number) =>
          `${i + 1}. ${c.name ?? 'ללא שם'} (${c.phone ?? 'ללא טלפון'}) — id: ${c.id}`
        );
        addMessage({
          id: crypto.randomUUID(),
          role: 'assistant',
          content:
            `מצאתי התאמות:\n${lines.join('\n')}\n\n` +
            `אפשר לכתוב: "מחק לקוח עם id <id>" או לבקש: "תראה שוב את הרשימה".`,
          timestamp: new Date(),
        });
      }

      setIsTyping(false);
      setMcpLoading(false);
      return;
    }

    // ג) שגיאה כללית מהשרת
    if (data?.error) {
      toast.error(data.error);
      setIsTyping(false);
      setMcpLoading(false);
      return;
    }

    // ד) יש תשובה מנוסחת
    if (data?.reply) {
      setTimeout(() => {
        if (!sentClientIds.current.has(clientReplyId)) {
          addMessage({
            id: clientReplyId,
            role: 'assistant',
            content: data.reply,
            timestamp: new Date(),
          });
          sentClientIds.current.add(clientReplyId);
          setIsTyping(false);
        }
      }, 3000);
      return;
    }

    // ה) אין reply אבל גם אין שגיאה — נסגור ספינר בכל מקרה
    setIsTyping(false);
    setMcpLoading(false);
  } catch (error: any) {

    toast.error(error?.message || 'שגיאה בשליחת ההודעה');
    setIsTyping(false);
    setMcpLoading(false);
  } finally {
    // חשוב מאוד: לוודא שהספינרים נסגרים בכל מסלול
    setMcpLoading(false);
    setIsTyping(false);
  }
};

// ========= handleConfirmDelete =========
const handleConfirmDelete = async () => {
  if (!pendingDelete) return;

  setShowDeleteModal(false);
  setIsTyping(true);
  setMcpLoading(true);

  const clientReplyId = crypto.randomUUID();

  try {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const res = await fetch('https://nkuqcyelxgyihrxyvitb.functions.supabase.co/mcp-agent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        businessId,
        userId: user?.id,
        message: pendingDelete.message,
        confirm: true,
        sql: pendingDelete.sql ?? null,
        clientReplyId,
        client_reply_id: clientReplyId,
      }),
    });

    const data = await res.json();

    if (data?.error) {
      addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.error,
        timestamp: new Date(),
      });
      setIsTyping(false);
      setMcpLoading(false);
      return;
    }

    if (data?.reply) {
      setTimeout(() => {
        if (!sentClientIds.current.has(clientReplyId)) {
          addMessage({
            id: clientReplyId,
            role: 'assistant',
            content: data.reply,
            timestamp: new Date(),
          });
          sentClientIds.current.add(clientReplyId);
        }
        setIsTyping(false);
      }, 3000);
      return;
    }

    // fallback: לכבות ספינרים גם אם אין reply
    setIsTyping(false);
    setMcpLoading(false);
  } catch (error: any) {

    toast.error(error?.message || 'שגיאה בשליחת ההודעה');
    setIsTyping(false);
    setMcpLoading(false);
  } finally {
    setMcpLoading(false);
    setIsTyping(false);
    setPendingDelete(null);
    setDeleteWarning('');
  }
};

  const handleStartRecording = async () => {
    if (blocked || !canRecord) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      
      setMediaRecorder(recorder);
      setIsRecording(true);
      recorder.start();

      const startTime = Date.now();
      const timer = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      setRecordingTimer(timer);

      recorder.ondataavailable = async (event) => {
        const blob = new Blob([event.data], { type: 'audio/webm' });
        try {
          const fileName = `${user?.id}/${Date.now()}.webm`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('chat_recordings')
            .upload(fileName, blob);

          if (uploadError) throw uploadError;

          const { data: urlData } = supabase.storage
            .from('chat_recordings')
            .getPublicUrl(fileName);

          const recordingUrl = urlData.publicUrl;

          const userMessage: Message = {
            id: crypto.randomUUID(),
            role: 'user',
            content: '🎤 הקלטה קולית',
            timestamp: new Date()
          };

          addMessage(userMessage);
          setIsTyping(true);

          await sendMessageToWebhook('', 'audio', recordingUrl);
        } catch (error: any) {

          toast.error(error.message || 'שגיאה בשליחת ההקלטה');
          setIsTyping(false);
        }
      };
    } catch (error: any) {

      if (error.name === 'NotAllowedError') {
        toast.error('יש לאשר גישה למיקרופון');
      } else if (error.name === 'SecurityError') {
        toast.error('יש לפתוח את האתר ב-HTTPS');
      } else {
        toast.error('שגיאה בהפעלת ההקלטה, נסה שוב');
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
      
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };

  const handleCancelRecording = () => {
    setRecordingBlob(null);
    setRecordingDuration(0);
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetails(true);
  };

  const handleCloseAppointmentDetails = () => {
    setShowAppointmentDetails(false);
    setSelectedAppointment(null);
  };

  const handleQuickReply = (e: React.MouseEvent, reply: string) => {
    e.preventDefault();
    if (blocked || userBlocked) return;
    handleSendMessage(reply);
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    if (blocked || userBlocked) return;
    action();
  };

  // דוגמא ל-shortcuts
  const shortcuts = [
    {
      label: 'ניהול לקוחות',
      icon: <Users className="w-6 h-6 text-purple-600" />,
      href: '/customers',
      bg: 'bg-purple-50',
      onClick: () => navigate('/customers'),
    },
    {
      label: 'דוחות וסטטיסטיקות',
      icon: <BarChart2 className="w-6 h-6 text-indigo-600" />,
      href: '/statistics',
      bg: 'bg-indigo-50',
      onClick: () => navigate('/statistics'),
    },
    {
      label: 'הגדרות מערכת',
      icon: <Settings className="w-6 h-6 text-pink-600" />,
      href: '/settings',
      bg: 'bg-pink-50',
      onClick: () => navigate('/settings'),
    },
    {
      label: 'מרכז העזרה',
      icon: <HelpCircle className="w-6 h-6 text-blue-600" />,
      href: '/help',
      bg: 'bg-blue-50',
      onClick: () => navigate('/help'),
    },
  ];

  const [showBuyTokensPopup, setShowBuyTokensPopup] = useState(false);
  const [buyTokensAmount, setBuyTokensAmount] = useState<number>(100);
  const [paymentStep, setPaymentStep] = useState<'amount' | 'details' | 'success'>('amount');
  const [paymentDetails, setPaymentDetails] = useState({
    name: '',
    email: '',
    card: '',
    cvv: '',
    id: '',
    expiry: '',
  });

  const handleBuyTokens = () => {
    setShowBuyTokensPopup(true);
    setPaymentStep('amount');
    setPaymentDetails({
      name: '',
      email: '',
      card: '',
      cvv: '',
      id: '',
      expiry: '',
    });
  };

  const handleBuyTokensNext = () => {
    setPaymentStep('details');
  };

  const handleBuyTokensPay = () => {
    // כאן תוכל להוסיף אינטגרציה אמיתית לפיליקארד
    setPaymentStep('success');
    setTimeout(() => setShowBuyTokensPopup(false), 2500);
  };

  // הוסף פונקציה שמבצעת איפוס טוקנים אם עברו 30 יום מהאיפוס האחרון
  function shouldResetTokens(tokensInfo: TokensInfo | null): boolean {
    if (!tokensInfo || !tokensInfo.tokens_limit) return false;
    // השתמש בשדה החדש tokens_last_reset
    const lastReset = tokensInfo.tokens_last_reset;
    if (!lastReset) return false;
    const lastResetDate = new Date(lastReset);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - lastResetDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 30;
  }

  // הוסף useEffect שמבצע איפוס אוטומטי אם צריך
  useEffect(() => {
    if (
      tokensInfo &&
      tokensInfo.tokens_limit &&
      shouldResetTokens(tokensInfo)
    ) {
      // קריאה ל-RPC או API שמאפס את הטוקנים (צריך להוסיף ב-backend)
      const resetTokens = async () => {
        try {
          const { error } = await supabase.rpc('reset_monthly_tokens', {
            p_business_id: businessId
          });
          if (!error) {
            toast.success('טוקנים חודשיים אופסו בהצלחה');
            // טען מחדש את המידע
            await loadTokensInfo(businessId!);
          } else {
            toast.error('שגיאה באיפוס הטוקנים');
          }
        } catch (err) {
          toast.error('שגיאה באיפוס הטוקנים');
        }
      };
      resetTokens();
    }
  }, [tokensInfo, businessId]);

  useEffect(() => {
    // בדוק ואפס טוקנים אם עברו 30 יום מהאיפוס האחרון (בכניסה לדשבורד)
    async function checkAndResetTokens() {
      if (!user?.id || !businessId) return;
      try {
        // שלוף לפי business_id במקום user_id, ואל תשתמש ב-single
        const { data: usage, error } = await supabase
          .from('token_usage')
          .select('*')
          .eq('business_id', businessId);

        if (error) {

          return;
        }
        if (!usage || usage.length === 0) {

          return;
        }

        // קח את הרשומה הראשונה (אם יש כמה)
        const usageRow = usage[0];
        if (!usageRow?.tokens_last_reset) {

          return;
        }

        const now = new Date();
        const lastReset = new Date(usageRow.tokens_last_reset);
        const daysSinceReset = (now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24);


        if (daysSinceReset >= 30) {
          await supabase
            .from('token_usage')
            .update({
              tokens_used: 0,
              tokens_last_reset: new Date().toISOString()
            })
            .eq('business_id', businessId);
          // רענן את המידע
          await loadTokensInfo(businessId);
        }
      } catch (err) {

      }
    }

    checkAndResetTokens();
  }, [user?.id, businessId]);

  const useKeyboardInset = () => {
  const [inset, setInset] = React.useState(0);

  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      // כמה גובה "נאבד" כשמקלדת פתוחה
      const lost = window.innerHeight - vv.height;
      // סף קטן כדי לא להדליק inset על שינויים מזעריים
      setInset(lost > 120 ? Math.round(lost) : 0);
    };

    onResize();
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  return inset;
};

  // נסה לרענן את ה-business/plan עד 3 פעמים אם אין planName
  // const [planLoadingTries, setPlanLoadingTries] = useState(0);

  // useEffect(() => {
  //   if (!planName && planLoadingTries < 3) {
  //     setTimeout(async () => {
  //       if (refreshBusiness) {
  //         await refreshBusiness();
  //       }
  //       setPlanLoadingTries(t => t + 1);
  //     }, 1200);
  //   }
  // }, [planName, planLoadingTries, refreshBusiness]);

  // Add this useEffect after your other useEffects in Dashboard
  useEffect(() => {
    if (!isLoadingBlockStatus && (blocked || userBlocked)) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [blocked, userBlocked, isLoadingBlockStatus]);

  // גלילה אוטומטית להודעה האחרונה כאשר ההודעות משתנות
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
  (async () => {
    if (!tokensInfo || !businessPlanId) return;

    // אם ה-RPC/VIEW לא החזירו תקרה – נשלים מהתכנית
    const needTokensCap  = tokensInfo.tokens_limit  == null;
    const needDisplayCap = tokensInfo.display_limit == null;
    if (!needTokensCap && !needDisplayCap) return;

    const plan = await fetchPlanLimitsById(businessPlanId);
    if (!plan) return;

    const tokensCap  = needTokensCap  ? plan.ai_tokens_limit  : tokensInfo.tokens_limit;
    const displayCap = needDisplayCap ? plan.ai_display_limit : tokensInfo.display_limit;

    const tokensRemaining =
      tokensCap == null
        ? tokensInfo.tokens_remaining ?? null
        : Math.max(0, (tokensCap ?? 0) - (tokensInfo.tokens_used ?? 0));

    const displayRemaining =
      displayCap == null
        ? tokensInfo.display_remaining ?? null
        : Math.max(0, (displayCap ?? 0) - (tokensInfo.display_used ?? 0));

    setTokensInfo(prev =>
      prev
        ? {
            ...prev,
            tokens_limit: tokensCap,
            display_limit: displayCap,
            tokens_remaining: tokensRemaining,
            display_remaining: displayRemaining,
          }
        : prev
    );
  })();
}, [tokensInfo, businessPlanId]);

  // מצב לפתיחת הצ'אט כ-popup
  const [showChatPopup, setShowChatPopup] = useState(false);
  // Lead form state
  const [leadFormSubmitted, setLeadFormSubmitted] = useState(false);
  const [leadFormData, setLeadFormData] = useState({ name: '', businessName: '', phone: '' });

  // === Inset דינמי למקלדת עבור מודאל הצ'אט ===
  const bottomInset = useKeyboardInset();

  // הסתירו את ה-BottomNav כשמודאל הצ'אט פתוח או כשמקלדת פתוחה
  useEffect(() => {
    const shouldHide = showChatPopup || bottomInset > 0;
    document.documentElement.classList.toggle('hide-bottom-nav', shouldHide);
    return () => {
      document.documentElement.classList.remove('hide-bottom-nav');
    };
  }, [showChatPopup, bottomInset]);

    useEffect(() => {
  if (showChatPopup && businessId) {
    loadTokensInfo(businessId);
  }
}, [showChatPopup, businessId]);

  // שלח אירוע גלובלי להסתרת BottomNav (לשימוש בקומפוננטות אחרות)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lf:bottomnav', { detail: { hide: showChatPopup } }));
    return () => {
      window.dispatchEvent(new CustomEvent('lf:bottomnav', { detail: { hide: false } }));
    };
  }, [showChatPopup]);

  // קבע האם החבילה היא basic לפי subscription.plan.id
  const BASIC_PLAN_IDS = [
    '686adae1-c8ab-46a8-aea4-d5a78e431723', // מזהה מסלול בסיסי
    // תוכל להוסיף כאן מזהים נוספים אם יש
  ];
  const planId = subscription?.plan?.id || null;
  const isBasicPlan = planId && BASIC_PLAN_IDS.includes(planId);

  // חסימה מוחלטת אם אין מנוי!
  if (!subscriptionLoading && !subscription) {
    return (
      <div dir="rtl" className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-200"
        >
          <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="h-10 w-10 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            יש לבחור חבילה כדי להמשיך
          </h2>
          <p className="text-gray-600 mb-8">
            כדי להתחיל להשתמש במערכת ולגשת לכל התכונות, יש לבחור חבילה מתאימה לעסק שלך
          </p>
          <button
            onClick={() => navigate('/plan-upgrade')}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
          >
            בחר חבילה עכשיו
          </button>
        </motion.div>
      </div>
    );
  }

  if (!businessId) {
    return (
      <LoadingScreen
        title="מקימים לך עסק…"
        subtitle="זה לוקח כמה שניות"
      />
    );
  }
  // --- אזור מילוי יומן (הצעות שיבוץ) ---
const renderCalendarFill = () => (
  <div className="w-full min-h-screen flex flex-col items-center justify-start bg-white py-4 px-1 border-t border-gray-200">
    <div className="flex flex-col items-center mb-4">
      <Calendar className="w-10 h-10 text-indigo-400 mb-1" />
      <h1 className="text-xl font-bold text-indigo-600 mb-1">מילוי יומן</h1>
      <p className="text-gray-500 text-base mb-2">בחר הצעות שיבוץ, מלא את היומן שלך בקלות, או השתמש במילוי אוטומטי!</p>
      <button
        className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-400 to-indigo-400 text-white font-bold shadow hover:from-purple-500 hover:to-indigo-500 transition mb-1 text-sm"
        style={{ minHeight: 36 }}
        onClick={() => setShowAutofillPopup(true)}
      >
        מלא את היומן אוטומטית
      </button>
    </div>
    <div className="w-full max-w-2xl mx-auto">
      {Object.keys(groupedProposals || {}).length === 0 ? (
        <div className="text-center text-gray-400 py-8 text-base">אין כרגע הצעות לשיבוץ. נסה מאוחר יותר או השתמש במילוי אוטומטי.</div>
      ) : (
        Object.entries(groupedProposals).map(([day, proposals]: any) => (
          <div key={day} className="mb-6">
            <h2 className="font-bold text-lg mb-2 text-indigo-600">
              {format(new Date(day), 'EEEE, d בMMMM', { locale: he })}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
              {proposals.map((p: any) => (
                <div key={p.id} className="bg-white rounded-xl p-3 shadow border border-gray-100 flex flex-col gap-2 hover:shadow-md transition" style={{ minHeight: 120 }}>
                  <div className="flex items-center gap-1 text-indigo-600">
                    <User className="w-4 h-4" />
                    <span className="font-bold text-base">{p.customer?.name}</span>
                    <span className="text-xs text-gray-400">({p.customer?.phone})</span>
                  </div>
                  <div className="flex items-center gap-1 text-purple-600 text-xs">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">איש צוות:</span>
                    <span className="font-bold">{p.staff?.name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-pink-600 text-xs">
                    <Scissors className="w-4 h-4" />
                    <span className="font-medium">שירות:</span>
                    <span className="font-bold">{p.service?.name_he}</span>
                    <span className="text-xs text-gray-400">({p.service?.price}₪, {p.service?.duration} דק')</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 text-xs">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">שעה:</span>
                    <span className="font-bold">{p.hour}</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button className="flex-1 px-2 py-1 rounded bg-indigo-500 text-white font-semibold hover:bg-indigo-600 transition text-xs" onClick={() => {/* offer */}}>
                      הצע ללקוח
                    </button>
                    <button className="flex-1 px-2 py-1 rounded bg-green-500 text-white font-semibold hover:bg-green-600 transition text-xs" onClick={() => {/* apply */}}>
                      שבץ ביומן
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full overflow-x-hidden" style={{ background: 'transparent' }}>
      {/* Main Header - fixed to top */}
      {!showChatPopup && (
        <div className="w-full fixed top-0 left-0 right-0 z-30 h-20 flex items-center" style={{ background: 'transparent' }}>
          {/* מחק את תוכן ה-header, השאר div ריק לשמירה על מבנה */}
        </div>
      )}
      {/* תורים הקרובים */}
      <div className="w-full bg-[#6D4AFF]">
        <div className="flex items-center gap-3 mb-2 pt-6 px-4">
          <div className="p-2 bg-white/20 rounded-xl">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <span className="text-lg font-bold text-white">התורים הקרובים שלך</span>
          <span
            className={`px-3 py-1 rounded-xl font-bold text-sm shadow border ${
              planName
                ? 'bg-yellow-100 border-yellow-300 text-yellow-900'
                : 'bg-red-100 border-red-300 text-red-700'
            }`}
            style={{ direction: 'rtl', cursor: planName ? 'pointer' : 'default' }}
            onClick={() => {
              if (planName) navigate('/plan-upgrade');
            }}
            title={planName ? 'שדרג/י חבילה' : undefined}
          >
            {planName ? (
              // הסר את ה-underline
              <span>{planName}</span>
            ) : (
              'לא נמצא מסלול'
            )}
          </span>
        </div>
        <p className="text-sm text-white/80 mb-2 px-4">
          {format(new Date(), 'EEEE, d בMMMM', { locale: he })}
        </p>
        <div className="relative px-4 pb-4">
          {loadingAppointments ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : appointments.length > 0 ? (
            <div className="overflow-x-auto pb-2 scrollbar-none">
              <div className="flex gap-3">
                {appointments.map((appointment) => {
                  const appointmentDate = parseISO(appointment.start_time);
                  const isToday = isSameDay(appointmentDate, new Date());
                  return (
                    <div
                      key={appointment.id}
                      onClick={() => handleAppointmentClick(appointment)}
                      className="flex-none w-[260px] bg-white/10 backdrop-blur-lg rounded-xl p-3 cursor-pointer border-2 border-white/20 flex flex-col justify-center items-center min-h-[110px]"
                    >
                      <div className="flex flex-col gap-2 w-full">
                        <div className="flex flex-row items-center justify-between w-full mb-1">
                          <h3 className="font-bold text-white text-base truncate text-right flex-1">
                            {appointment.customer_name}
                          </h3>
                          <div className="flex flex-col items-end ml-2">
                            <div className="flex flex-row items-center gap-2">
                              <span className="block text-amber-200 text-xs font-semibold">
                                {isToday ? 'היום' : format(appointmentDate, 'EEEE', { locale: he })}
                              </span>
                              <span className="text-sm text-white font-semibold">
                                {format(appointmentDate, 'HH:mm')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-row items-center justify-between w-full text-sm mt-2">
                          <div className="flex items-center gap-2 text-white">
                            <Scissors className="h-4 w-4 text-white" />
                            <span className="font-medium text-white">{appointment.service_name}</span>
                          </div>
                          <span className="font-bold text-white text-lg">₪{appointment.price}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-white/80">
              אין תורים פעילים בשבוע הקרוב
            </div>
          )}
        </div>
      </div>
      {/* סטטיסטיקות - sticky header for statistics */}
      <div className="wולד" style={{ background: 'transparent' }}>
        <div className="sticky top-20 z-20 w-full" style={{ background: 'transparent' }}>
          <StatisticsPage />
        </div>
      </div>
      {/* Lolyta AI - Premium Assistant */}
      <LoLytaAI
        tokensInfo={tokensInfo}
        isBlocked={isChatBlocked(tokensInfo, isDashboardPage, trialAvailable)}
      />


<AnimatePresence>
  {showDeleteModal && (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40"
      onClick={() => setShowDeleteModal(false)}
    >
      <div
        className="bg-white rounded-xl p-6 max-w-sm w-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-red-600 mb-4">{deleteWarning}</h2>
        <div className="flex gap-4">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            ביטול
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            אישור מחיקה
          </button>
        </div>
      </div>
    </motion.div>
  )}
</AnimatePresence>

      {/* פופאפ רכישת טוקנים */}
      <AnimatePresence>
        {showBuyTokensPopup && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowBuyTokensPopup(false)}
          >
            <div
              className="rounded-xl shadow-xl p-8 max-w-sm w-full flex flex-col items-center"
              onClick={e => e.stopPropagation()}
              style={{ background: 'transparent' }}
            >
              <h2 className="text-xl font-bold text-indigo-700 mb-4">רכישת טוקנים</h2>
              {paymentStep === 'amount' && (
                <>
                  <label className="mb-2 font-semibold">בחר כמות טוקנים:</label>
                  <input
                    type="number"
                    min={10}
                    max={10000}
                    value={buyTokensAmount}
                    onChange={e => setBuyTokensAmount(Number(e.target.value))}
                    className="border rounded px-3 py-2 mb-4 w-full text-center"
                  />
                  <button
                    className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold shadow hover:from-purple-600 hover:to-indigo-600 transition"
                    onClick={handleBuyTokensNext}
                  >
                    המשך
                  </button>
                </>
              )}
              {paymentStep === 'details' && (
                <>
                  <label className="mb-2 font-semibold">פרטי תשלום:</label>
                  <input
                    type="text"
                    placeholder="שם מלא"
                    value={paymentDetails.name}
                    onChange={e => setPaymentDetails({ ...paymentDetails, name: e.target.value })}
                    className="border rounded px-3 py-2 mb-2 w-full"
                  />
                  <input
                    type="email"
                    placeholder="אימייל"
                    value={paymentDetails.email}
                    onChange={e => setPaymentDetails({ ...paymentDetails, email: e.target.value })}
                    className="border rounded px-3 py-2 mb-2 w-full"
                  />
                  <input
                    type="text"
                    placeholder="מספר כרטיס"
                    value={paymentDetails.card}
                    onChange={e => setPaymentDetails({ ...paymentDetails, card: e.target.value })}
                    className="border rounded px-3 py-2 mb-2 w-full"
                  />
                  <input
                    type="text"
                    placeholder="CVV"
                    value={paymentDetails.cvv}
                    onChange={e => setPaymentDetails({ ...paymentDetails, cvv: e.target.value })}
                    className="border rounded px-3 py-2 mb-2 וfull"
                  />
                  <input
                    type="text"
                    placeholder="תוקף (MM/YY)"
                    value={paymentDetails.expiry}
                    onChange={e => setPaymentDetails({ ...paymentDetails, expiry: e.target.value })}
                    className="border rounded px-3 py-2 mb-2 w-full"
                  />
                  <button
                    className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold shadow hover:from-purple-600 hover:to-indigo-600 transition mt-2"
                    onClick={handleBuyTokensPay}
                  >
                    שלם
                  </button>
                </>
              )}
              {paymentStep === 'success' && (
                <div className="text-green-600 font-bold text-lg mt-4">
                  הרכישה בוצעה בהצלחה!
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* AutofillScheduler Modal */}
      <AnimatePresence>
        {showAutofillPopup && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/40 p-4"
            onClick={() => setShowAutofillPopup(false)}
          >
            <div
              className="bg-white rounded-xl p-4 w-full max-w-3xl relative"
              onClick={e => e.stopPropagation()}
              style={{ minHeight: 320 }}
            >
              <button
                onClick={() => setShowAutofillPopup(false)}
                className="absolute left-4 top-4 text-2xl text-gray-500 hover:text-gray-700"
                style={{ zIndex: 10 }}
                aria-label="סגור"
              >
                ✕
              </button>
              <AutofillUI
                edgeUrl={`${supabase.url}/functions/v1/mcp-agent`}
                defaultBusinessId={business?.id || ""}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Appointment Details Popup */}
      <AnimatePresence>
      {showAppointmentDetails && selectedAppointment && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 40 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={handleCloseAppointmentDetails}
          style={{ pointerEvents: 'auto' }}
        >
          <div
            className="rounded-xl shadow-xl p-6 max-w-md w-full relative flex flex-col"
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', minHeight: 320 }}
          >
            <button
              onClick={handleCloseAppointmentDetails}
              className="absolute left-4 top-4 text-2xl text-gray-500 hover:text-gray-700"
              style={{ zIndex: 10 }}
              aria-label="סגור"
            >
              ✕
            </button>
            <AppointmentDetails
              appointment={selectedAppointment}
              onClose={handleCloseAppointmentDetails}
              onUpdate={() => {
                setShowAppointmentDetails(false);
                setSelectedAppointment(null);
                // רענן את התורים אחרי עדכון
                if (businessId) loadAppointments(businessId);
              }}
            />
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

export default Dashboard;